import { useQuery } from "@tanstack/react-query";

import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";

const toQueryString = (query?: Record<string, string | number | boolean | undefined>) => {
  if (!query) {
    return "";
  }
  const searchParams = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.set(key, `${value}`);
    }
  });
  const encoded = searchParams.toString();
  return encoded.length ? `?${encoded}` : "";
};

export interface FamilyModelChoice {
  value: string;
  name: string;
  isActive: boolean;
  internalCode?: string;
}

export interface FamilyDataConstants {
  roles: FamilyModelChoice[];
  relationships: FamilyModelChoice[];
  titles: FamilyModelChoice[];
  genders: FamilyModelChoice[];
  grades: FamilyModelChoice[];
  preferredContactMethods: FamilyModelChoice[];
}

export interface FamilySummary {
  id: string;
  name: string;
}

export interface FamiliesResponse {
  families: FamilySummary[];
}

export interface FamilyRoleAssignment {
  contact_id: number;
  role_id: string;
}

export interface FamilyRelationshipAssignment {
  subjectContactId: number;
  objectContactId: number;
  relationshipId: string;
}

export interface UpsertFamilyPayload {
  name: string;
  roles: FamilyRoleAssignment[];
  relationships: FamilyRelationshipAssignment[];
}

export type UpsertFamilyResponse = FamilySummary;

export interface CenterFamilyContactDetail {
  id: number;
  firstName: string;
  lastName: string;
  displayName: string;
  email?: string;
  personalPhone?: string;
  homePhone?: string;
  businessPhone?: string;
  schoolName?: string;
  grade?: FamilyModelChoice;
  preferredContactMethod?: string;
}

export interface CenterFamilyContactBasePayload {
  firstName: string;
  lastName: string;
  age?: number;
  email: string;
  grade_id?: string;
  title?: string;
  suffix?: string;
  gender?: string;
  dob?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  personalPhone?: string;
  homePhone?: string;
  businessPhone?: string;
  schoolName?: string;
  preferredContactMethod?: string;
}

export type CreateCenterFamilyContactPayload = CenterFamilyContactBasePayload;

export type UpdateCenterFamilyContactPayload = CenterFamilyContactBasePayload;

export interface FamilyContactLookupItem {
  id: number;
  displayName: string;
}

export const getFamilyDataConstants = (accessToken: string) => {
  return callLcosService<FamilyDataConstants>(accessToken, "/api/data/constants/families", "GET");
};

export const getCenterFamilies = (
  accessToken: string,
  centerId: string,
  query?: Record<string, string | number | boolean | undefined>
) => {
  return callLcosService<FamiliesResponse>(
    accessToken,
    `/api/centers/${centerId}/families${toQueryString(query)}`,
    "GET"
  );
};

export const getCenterContactFamilies = (
  accessToken: string,
  centerId: string,
  query?: Record<string, string | number | boolean | undefined>
) => {
  return callLcosService<FamiliesResponse>(
    accessToken,
    `/api/centers/${centerId}/families/contact_families${toQueryString(query)}`,
    "GET"
  );
};

export const getCenterFamilyContacts = (
  accessToken: string,
  centerId: string,
  query?: Record<string, string | number | boolean | undefined>
) => {
  return callLcosService<{ contacts: CenterFamilyContactDetail[] }>(
    accessToken,
    `/api/centers/${centerId}/families/contacts${toQueryString(query)}`,
    "GET"
  );
};

export const getCenterFamilyContact = (accessToken: string, centerId: string, contactId: number) => {
  return callLcosService<CenterFamilyContactDetail>(
    accessToken,
    `/api/centers/${centerId}/families/contacts/${contactId}`,
    "GET"
  );
};

export const updateCenterFamilyContact = (
  accessToken: string,
  centerId: string,
  contactId: number,
  payload: UpdateCenterFamilyContactPayload
) => {
  return callLcosService<CenterFamilyContactDetail>(
    accessToken,
    `/api/centers/${centerId}/families/contacts/${contactId}`,
    "PUT",
    payload
  );
};

export const updateCenterFamily = (
  accessToken: string,
  centerId: string,
  familyId: string,
  payload: UpsertFamilyPayload
) => {
  return callLcosService<UpsertFamilyResponse>(
    accessToken,
    `/api/centers/${centerId}/families/${familyId}`,
    "PUT",
    payload
  );
};

export const createCenterFamilyContact = (
  accessToken: string,
  centerId: string,
  payload: CreateCenterFamilyContactPayload
) => {
  return callLcosService<CenterFamilyContactDetail>(
    accessToken,
    `/api/centers/${centerId}/families/contacts`,
    "POST",
    payload
  );
};

export const getFamiliesContacts = (
  accessToken: string,
  centerId: string,
  query?: Record<string, string | number | boolean | undefined>
) => {
  return callLcosService<{ contacts: CenterFamilyContactDetail[] }>(
    accessToken,
    `/api/centers/${centerId}/families/contacts${toQueryString(query)}`,
    "GET"
  );
};

export const createCenterFamily = (accessToken: string, centerId: string, payload: UpsertFamilyPayload) => {
  return callLcosService<UpsertFamilyResponse>(accessToken, `/api/centers/${centerId}/families`, "POST", payload);
};

export const createFamily = (accessToken: string, centerId: string, payload: UpsertFamilyPayload) => {
  return createCenterFamily(accessToken, centerId, payload);
};

export const updateFamily = (accessToken: string, centerId: string, familyId: string, payload: UpsertFamilyPayload) => {
  return callLcosService<UpsertFamilyResponse>(
    accessToken,
    `/api/centers/${centerId}/families/${familyId}`,
    "PUT",
    payload
  );
};

export const useFamilyDataConstants = (enabled = false) => {
  const { getToken } = useAccessToken();
  return useQuery({
    queryKey: ["familyDataConstants"],
    enabled,
    queryFn: async () => getFamilyDataConstants(await getToken())
  });
};
